import { useState } from "react";
import {
  apiAddCollaborators,
  type SearchedUser,
} from "../data/api";
import { UserPicker } from "../share/UserPicker";
import { CollaboratorList } from "../share/CollaboratorList";

import "./ShareInternalDialog.scss";

type Permission = "view" | "edit";

interface ShareInternalDialogProps {
  drawingId: string;
  drawingName: string;
  onClose: () => void;
}

export const ShareInternalDialog = ({
  drawingId,
  drawingName,
  onClose,
}: ShareInternalDialogProps) => {
  const [selected, setSelected] = useState<SearchedUser[]>([]);
  const [permission, setPermission] = useState<Permission>("view");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Incrementado a cada convite enviado, para recarregar a lista de acesso.
  const [reloadToken, setReloadToken] = useState(0);

  const handleSend = async () => {
    setError(null);
    if (selected.length === 0) {
      setError("Selecione ao menos um usuário");
      return;
    }
    setSending(true);
    try {
      await apiAddCollaborators(drawingId, {
        userIds: selected.map((u) => u.id),
        permission,
        message: message.trim() || undefined,
      });
      // Mantém o diálogo aberto para mostrar a lista de acesso atualizada.
      setSuccess(true);
      setSelected([]);
      setMessage("");
      setReloadToken((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao compartilhar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="share-internal-overlay" onClick={onClose}>
      <div
        className="share-internal-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="share-internal-dialog__close"
          onClick={onClose}
          aria-label="Fechar"
        >
          &times;
        </button>

        <header className="share-internal-dialog__header">
          <h2>Compartilhar quadro</h2>
          <p className="share-internal-dialog__subtitle">
            Convidar pessoas para acessar <strong>{drawingName}</strong>
          </p>
        </header>

        <div className="share-internal-dialog__body">
          <div className="share-internal-dialog__field">
            <label>Pessoas</label>
            <UserPicker
              value={selected}
              onChange={(users) => {
                setSelected(users);
                setSuccess(false);
              }}
              disabled={sending}
              allowSelectAll
            />
          </div>

          <div className="share-internal-dialog__field">
            <label>Permissão</label>
            <div className="share-internal-dialog__perm">
              <button
                type="button"
                className={permission === "view" ? "active" : ""}
                onClick={() => setPermission("view")}
                disabled={sending}
              >
                <strong>Visualizar</strong>
                <span>Apenas ver, sem editar</span>
              </button>
              <button
                type="button"
                className={permission === "edit" ? "active" : ""}
                onClick={() => setPermission("edit")}
                disabled={sending}
              >
                <strong>Editar</strong>
                <span>Pode alterar o quadro</span>
              </button>
            </div>
          </div>

          <div className="share-internal-dialog__field">
            <label htmlFor="share-msg">Mensagem (opcional)</label>
            <textarea
              id="share-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Adicione uma nota para quem está recebendo..."
              maxLength={500}
              disabled={sending}
              rows={3}
            />
          </div>

          {error && <div className="share-internal-dialog__error">{error}</div>}
          {success && (
            <div className="share-internal-dialog__success">
              Convite enviado!
            </div>
          )}

          <CollaboratorList drawingId={drawingId} reloadToken={reloadToken} />
        </div>

        <footer className="share-internal-dialog__actions">
          <button
            className="share-internal-dialog__btn"
            onClick={onClose}
            disabled={sending}
          >
            Cancelar
          </button>
          <button
            className="share-internal-dialog__btn share-internal-dialog__btn--primary"
            onClick={handleSend}
            disabled={sending || selected.length === 0}
          >
            {sending ? "Enviando..." : "Compartilhar"}
          </button>
        </footer>
      </div>
    </div>
  );
};
